import { ChangeEvent, MutableRefObject, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormik, FormikErrors, FormikTouched, getIn } from 'formik'
import { Typography } from '@material-ui/core'

import { KnowledgeBaseContentType } from 'types/KnowledgeBaseContentType'
import { useIsMobile } from 'hooks/useIsMobile'
import { Translation } from 'types/Translation'
import { Label } from 'types/Label'
import {
  KnowledgeBaseContentFormFields,
  QuizSubForm,
  PresentationTestSubForm,
} from './types'
import { fillTranslations, getDefaultValues, getValidationSchema } from './utils'
import { QuizFormFields } from './KnowledgeBaseContentFormFields/QuizFormFields'
import { PresentationTestFormFields } from './KnowledgeBaseContentFormFields/PresentationTestFormFields'
import * as Styled from './KnowledgeBaseContentForm.styles'

interface KnowledgeBaseContentFormProps {
  initialValues?: KnowledgeBaseContentFormFields
  initialDestination?: number
  destinationPlaceholder?: string
  onSubmit?: (values: KnowledgeBaseContentFormFields, languageCode: string) => void
  isLoading?: boolean
  isError?: boolean
  submitRef?: MutableRefObject<() => void>
  initialLabels?: Label[]
}

const KnowledgeBaseContentForm: React.FC<KnowledgeBaseContentFormProps> = ({
  initialValues,
  initialDestination,
  destinationPlaceholder,
  isError,
  isLoading,
  initialLabels,
  onSubmit = () => {},
  submitRef,
}) => {
  const { t, i18n: { language: userLanguageBrowserCode } } = useTranslation()
  const isMobile = useIsMobile()

  const [translationLanguages, setTranslationLanguages] = useState<string[]>(
    fillTranslations(
      initialValues?.nameTranslations,
      initialValues?.descriptionTranslations,
      initialValues?.type !== KnowledgeBaseContentType.FILE
    ).nameTranslations?.map((value) => value.languageCode)
  )

  useEffect(() => {
    setTranslationLanguages(fillTranslations(
      initialValues?.nameTranslations,
      initialValues?.descriptionTranslations,
      initialValues?.type !== KnowledgeBaseContentType.FILE
    ).nameTranslations?.map((value) => value.languageCode))
  }, [initialValues])

  const isEditing = !!initialValues

  const submit = (values: KnowledgeBaseContentFormFields) => {
    onSubmit(values, userLanguageBrowserCode)
  }

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm,
    setValues,
    setTouched,
    setErrors,
  } = useFormik<KnowledgeBaseContentFormFields>({
    initialValues: {
      ...getDefaultValues(KnowledgeBaseContentType.PRESENTATION),
      destination: initialDestination || null,
      ...initialValues,
      ...fillTranslations(
        initialValues?.nameTranslations,
        initialValues?.descriptionTranslations,
        initialValues?.type !== KnowledgeBaseContentType.FILE
      ),
    },
    validationSchema: getValidationSchema(isEditing),
    onSubmit: submit,
    enableReinitialize: true,
  })

  useEffect(() => {
    if (submitRef?.current) {
      submitRef.current = handleSubmit
    }
    return () => {
      if (submitRef?.current) {
        submitRef.current = () => {}
      }
    }
  }, [handleSubmit, submitRef])

  const getValueForLanguageCode = (translations: Translation[], languageCode: string) => (
    translations.find((translation) => translation.languageCode === languageCode)?.value
  )

  const handleChangeTranslationLanguages = (event: ChangeEvent<{ value: unknown }>) => {
    const newTranslationLanguages = (event.target.value as string[]).sort()
    setTranslationLanguages(newTranslationLanguages)
    const currentNameTranslations = values.nameTranslations
    setFieldValue('nameTranslations', newTranslationLanguages.map((lng) => ({
      languageCode: lng,
      value: getValueForLanguageCode(currentNameTranslations, lng) || ''
    })))
    if (values.type !== KnowledgeBaseContentType.FILE) {
      const currentDescriptionTranslations = values.descriptionTranslations
      setFieldValue('descriptionTranslations', newTranslationLanguages.map((lng) => ({
        languageCode: lng,
        value: getValueForLanguageCode(currentDescriptionTranslations, lng) || ''
      })))
    }
  }

  const handleImageChange = (file: File) => {
    setFieldValue('image', file || null)
    setFieldTouched('image', true, false)
    validateForm({ ...values, image: file })
  }

  return (
    <Styled.Form onSubmit={handleSubmit}>
      {!submitRef && (
        <Styled.HeaderContainer>
          <Typography variant='h2'>{t('knowledgeBase.content')}</Typography>
          <Styled.SaveButton
            $isMobile={isMobile}
            type='submit'
            isLoading={isLoading}
            disabled={isError}
          >
            {t('common.save')}
          </Styled.SaveButton>
        </Styled.HeaderContainer>
      )}
      <Styled.InfoSection
        label={t('knowledgeBase.destination')}
        editValueComponent={
          <Styled.DestinationValue>
            {destinationPlaceholder}
          </Styled.DestinationValue>
        }
      />
      <Styled.InfoSection
        label={t('common.type')}
        editValueComponent={
          <Styled.GroupSelect
            id='type'
            name='type'
            label=''
            disabled={isEditing}
            value={values.type}
            onChange={(event) => {
              const newType = event.target.value as KnowledgeBaseContentType
              setValues(getDefaultValues(newType, values))
              setTouched({})
              setErrors({})
            }}
            onBlur={handleBlur}
            touched={!!touched.type}
            errorMessage={t(errors.type as unknown as string || '')}
            without={[
              KnowledgeBaseContentType.ALL,
              KnowledgeBaseContentType.GROUP,
              ...(isEditing ? [] : [KnowledgeBaseContentType.FILE])
            ]}
          />
        }
      />
      <Styled.InfoSection
        label={t('common.visibility')}
        editValueComponent={
          <Styled.KnowledgeBaseVisibilitySelect
            id='visibility'
            name='visibility'
            label=''
            value={values.visibility}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={!!touched.visibility}
            errorMessage={t(errors.visibility as unknown as string || '')}
          />
        }
      />

      <Styled.InfoSection
        label={t('common.name')}
        editValueComponent={
          <Styled.TextField
            id='name'
            name='name'
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.name}
            errorMessage={t(errors.name || '')}
          />
          }
      />
      {values.type !== KnowledgeBaseContentType.FILE && (
        <Styled.InfoSection
          label={t('common.description')}
          editValueComponent={
            <Styled.TextField
              id='description'
              name='description'
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched.description}
              errorMessage={t(errors.description || '')}
              multiline
              minRows={4}
            />
        }
        />
      )}
      <Styled.InfoSection
        label={t('knowledgeBase.translations')}
        editValueComponent={
          <Styled.TranslationLanguagesSelect
            multiple
            value={translationLanguages}
            onChange={handleChangeTranslationLanguages}
          />
        }
      />
      {translationLanguages.map((languageCode, index) => (
        <>
          <Styled.InfoSection
            key={`${languageCode}-name`}
            label={`${t('common.name')} - ${languageCode}`}
            editValueComponent={
              <Styled.TextField
                id={`nameTranslations[${index}].value`}
                name={`nameTranslations[${index}].value`}
                value={values.nameTranslations[index].value}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={getIn(touched, `nameTranslations[${index}].value`)}
                errorMessage={t(getIn(errors, `nameTranslations[${index}].value`) || '')}
              />
            }
          />
          {values.type !== KnowledgeBaseContentType.FILE && (
          <Styled.InfoSection
            key={`${languageCode}-description`}
            label={`${t('common.description')} - ${languageCode}`}
            editValueComponent={
              <Styled.TextField
                id={`descriptionTranslations[${index}].value`}
                name={`descriptionTranslations[${index}].value`}
                value={values.descriptionTranslations[index].value}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={getIn(touched, `descriptionTranslations[${index}].value`)}
                errorMessage={t(getIn(errors, `descriptionTranslations[${index}].value`) || '')}
              />
            }
          />
          )}
        </>
      ))}

      <Styled.InfoSection
        label={t('knowledgeBase.photo')}
        editValueComponent={
          <Styled.UploadImageField
            disabled={!isEditing}
            error={!!errors.image}
            errorMessage={t(errors.image || '')}
            touched={touched.image}
            onFileChange={handleImageChange}
            file={values.image}
          />
        }
      />
      <Styled.InfoSection
        label={t('common.labels')}
        editValueComponent={
          <Styled.LabelSelect
            id='labelIds'
            name='labelIds'
            value={values.labelIds}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.labelIds}
            multiple
            initialData={initialLabels}
          />
        }
      />
      {values.type !== KnowledgeBaseContentType.QUIZ
      && values.type !== KnowledgeBaseContentType.FILE && (
        <PresentationTestFormFields
          onBlur={handleBlur}
          onChange={handleChange}
          setFieldValue={setFieldValue}
          contentValues={values.subForm}
          touched={touched}
          errors={errors}
          values={{
            duration: values.duration,
          }}
          contentTouched={touched.subForm as FormikTouched<PresentationTestSubForm>}
          contentErrors={errors.subForm as FormikErrors<PresentationTestSubForm>}
        />
      )}
      {values.type === KnowledgeBaseContentType.QUIZ && (
        <QuizFormFields
          isEditing={isEditing}
          onBlur={handleBlur}
          onChange={handleChange}
          setFieldValue={setFieldValue}
          quizValues={values.subForm}
          touched={touched}
          errors={errors}
          values={{
            duration: values.duration,
          }}
          quizTouched={touched.subForm as FormikTouched<QuizSubForm>}
          quizErrors={errors.subForm as FormikErrors<QuizSubForm>}
        />
      )}
    </Styled.Form>
  )
}

export type { KnowledgeBaseContentFormFields }
export { KnowledgeBaseContentForm }
